/**
 * useOrderDetails Hook
 * Custom hook for fetching and managing order details
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useState, useEffect, useCallback } from 'react';
import { orderService, OrderApiError, CheckPaymentSyncResponse, AutoCreateTransportResponse } from '@/services/commerce/orderService';
import type { OrderDetails, PaymentLinkResponse, PaymentStatusResponse } from '@/types/order';

interface UseOrderDetailsReturn {
  order: OrderDetails | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<boolean>;
  confirmOrder: (remarks?: string) => Promise<void>;
  cancelOrder: (reason: string) => Promise<void>;
  markReady: () => Promise<void>;
  scheduleDelivery: (deliveryDate: string) => Promise<void>;
  setOrder: React.Dispatch<React.SetStateAction<OrderDetails | null>>;
  // Payment methods
  generatePaymentLink: () => Promise<PaymentLinkResponse>;
  refreshPaymentStatus: () => Promise<PaymentStatusResponse>;
  checkAndSyncPaymentStatus: () => Promise<CheckPaymentSyncResponse>;
  markPaymentAsPaid: () => Promise<CheckPaymentSyncResponse>;
  autoCreateTransportRequest: () => Promise<AutoCreateTransportResponse>;
  paymentLoading: boolean;
}

/**
 * Custom hook to fetch and manage order details
 *
 * @param orderId - Order ID to fetch
 * @returns Order state and action methods
 */
export function useOrderDetails(orderId: number | string): UseOrderDetailsReturn {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);

  /**
   * Fetch order details from API
   */
  const fetchOrder = useCallback(async (): Promise<boolean> => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const numericOrderId = typeof orderId === 'string' ? parseInt(orderId, 10) : orderId;

      if (isNaN(numericOrderId)) {
        throw new Error('Invalid order ID format');
      }

      const data = await orderService.getOrderById(numericOrderId);
      setOrder(data);
      return true;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to load order details';
      setError(errorMessage);
      console.error('Error fetching order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  /**
   * Refetch order details (for manual refresh)
   */
  const refetch = useCallback(async (): Promise<boolean> => {
    return await fetchOrder();
  }, [fetchOrder]);

  /**
   * Confirm the order
   */
  const confirmOrder = useCallback(
    async (remarks?: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.confirmOrder(order.id, remarks);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to confirm order';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  /**
   * Cancel the order
   */
  const cancelOrder = useCallback(
    async (reason: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.cancelOrder(order.id, reason);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to cancel order';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  /**
   * Mark order as ready for dispatch
   */
  const markReady = useCallback(async () => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      const updatedOrder = await orderService.markReady(order.id);
      setOrder(updatedOrder);
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to mark order as ready';
      throw new Error(errorMessage);
    }
  }, [order]);

  /**
   * Schedule delivery for the order
   */
  const scheduleDelivery = useCallback(
    async (deliveryDate: string) => {
      if (!order) {
        throw new Error('Order not loaded');
      }

      try {
        const updatedOrder = await orderService.scheduleDelivery(order.id, deliveryDate);
        setOrder(updatedOrder);
      } catch (err) {
        const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to schedule delivery';
        throw new Error(errorMessage);
      }
    },
    [order]
  );

  /**
   * Generate payment link for the order (Cashfree)
   * Sends payment link via email to buyer
   */
  const generatePaymentLink = useCallback(async (): Promise<PaymentLinkResponse> => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      setPaymentLoading(true);
      const paymentResponse = await orderService.generatePaymentLink(order.id);

      // Update order with payment link info
      setOrder((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          paymentLink: paymentResponse.paymentLink,
          cashfreeOrderId: paymentResponse.cashfreeOrderId,
          payment: {
            ...prev.payment,
            paymentStatus: paymentResponse.paymentStatus,
            paymentLink: paymentResponse.paymentLink,
            cashfreeOrderId: paymentResponse.cashfreeOrderId,
          },
        };
      });

      return paymentResponse;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to generate payment link';
      throw new Error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  }, [order]);

  /**
   * Refresh payment status from backend
   * Used to poll for payment completion
   */
  const refreshPaymentStatus = useCallback(async (): Promise<PaymentStatusResponse> => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      setPaymentLoading(true);
      const paymentStatus = await orderService.getPaymentStatus(order.id);

      // Update order with latest payment status
      setOrder((prev) => {
        if (!prev) return prev;
        const newPaidAmount = paymentStatus.amountPaid || prev.payment.paidAmount;
        const newBalanceDue = Math.max(0, prev.payment.totalAmount - newPaidAmount);
        return {
          ...prev,
          paymentLink: paymentStatus.paymentLink,
          cashfreeOrderId: paymentStatus.cashfreeOrderId,
          paymentCompletedAt: paymentStatus.paymentCompletedAt,
          payment: {
            ...prev.payment,
            paymentStatus: paymentStatus.paymentStatus,
            paidAmount: newPaidAmount,
            balanceDue: newBalanceDue,
            paymentLink: paymentStatus.paymentLink,
            cashfreeOrderId: paymentStatus.cashfreeOrderId,
            paymentCompletedAt: paymentStatus.paymentCompletedAt,
          },
        };
      });

      return paymentStatus;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to refresh payment status';
      throw new Error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  }, [order]);

  /**
   * Check and sync payment status from Cashfree
   * Used for local testing when webhooks can't reach localhost
   */
  const checkAndSyncPaymentStatus = useCallback(async (): Promise<CheckPaymentSyncResponse> => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      setPaymentLoading(true);
      const syncResponse = await orderService.checkAndSyncPaymentStatus(order.id);

      // If payment was updated, refresh the entire order
      if (syncResponse.paymentUpdated || syncResponse.newPaymentStatus === 'PAID') {
        await fetchOrder();
      }

      return syncResponse;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to sync payment status';
      throw new Error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  }, [order, fetchOrder]);

  /**
   * Mark payment as paid (FOR TESTING ONLY)
   * Manually marks payment as complete for local testing
   */
  const markPaymentAsPaid = useCallback(async (): Promise<CheckPaymentSyncResponse> => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      setPaymentLoading(true);
      const markResponse = await orderService.markPaymentAsPaid(order.id);

      // Refresh order to get updated status
      await fetchOrder();

      return markResponse;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to mark payment as paid';
      throw new Error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  }, [order, fetchOrder]);

  /**
   * Auto-create transport request for the order
   * Creates transport request automatically for paid orders
   */
  const autoCreateTransportRequest = useCallback(async (): Promise<AutoCreateTransportResponse> => {
    if (!order) {
      throw new Error('Order not loaded');
    }

    try {
      setPaymentLoading(true);
      const transportResponse = await orderService.autoCreateTransportRequest(order.id);

      // Refresh order to get updated transport info
      await fetchOrder();

      return transportResponse;
    } catch (err) {
      const errorMessage = err instanceof OrderApiError ? err.message : 'Failed to create transport request';
      throw new Error(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  }, [order, fetchOrder]);

  // Fetch order on mount and when orderId changes
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    refetch,
    confirmOrder,
    cancelOrder,
    markReady,
    scheduleDelivery,
    setOrder,
    // Payment methods
    generatePaymentLink,
    refreshPaymentStatus,
    checkAndSyncPaymentStatus,
    markPaymentAsPaid,
    autoCreateTransportRequest,
    paymentLoading,
  };
}
